// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Reconciliation Dashboard
// Pipeline health, action queue, KPIs, zone progress
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SimpleGrid, Paper, Text, Group, Badge, Stack, Table, Loader, Center, Button, Progress,
} from '../../components/gab/GabComponents';
import {
  IconLeaf, IconFileInvoice, IconBuildingWarehouse,
  IconUsers, IconClock, IconCurrencyDollar, IconShieldCheck,
  IconAlertTriangle, IconArrowRight, IconCircleCheck,
  IconScale, IconPackage, IconTruck, IconRoute,
} from '@tabler/icons-react';
import { useDebrisStore } from '../../store/debrisStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useTampaDebrisStore } from '../../store/tampaDebrisStore';
import { useAuthStore } from '../../store/authStore';
import { useAllTickets } from '../../hooks/useAllTickets';
import { DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS } from '../../types/debris';
import type { DebrisType } from '../../types/debris';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { formatCurrency } from '../../lib/formatUtils';

// ── Status badge colors ──────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  active: 'var(--color-success)', pending: '#C68700', completed: 'var(--color-primary-700)', closed: '#939598',
  open: 'var(--color-success)', full: '#F86A0B',
  draft: '#94a3b8', submitted: '#3b82f6', approved: 'var(--color-success)', paid: '#059669',
  disputed: 'var(--color-error)', in_review: '#d97706', resolved: 'var(--color-success)',
  processing: '#3b82f6', failed: 'var(--color-error)',
};

// ── Import status colors ──────────────────────────────────────
const IMPORT_STATUS_COLORS: Record<string, string> = {
  pending: '#C68700', processing: '#3b82f6', completed: 'var(--color-success)', failed: 'var(--color-error)',
};

// ── Force account + document types ────────────────────────────
interface FALabor { employeeName: string; classification: string; hoursRegular: number; hoursOvertime: number; hourlyRate: number; totalCost: number; }
interface FAEquipment { fleetNumber: string; equipmentType: string; hoursUsed: number; hourlyRate: number; totalCost: number; }
interface DebrisDoc { documentType: string; title: string; }

export default function DebrisDashboard() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    zones, contractors, disposalSites, trucks,
    loadingZones, loadingContractors, loadingSites,
    loadZones, loadContractors, loadTrucks, loadDisposalSites,
  } = useDebrisStore();

  const {
    invoices,
    loadInvoices,
  } = useDebrisOpsStore();

  // Shared cached hook — all tickets for active incident (full meta including TDMS fields)
  const { tickets: allTickets, loading: loadingTickets } = useAllTickets();

  const {
    imports, reconciliations, femaPackages, loadingImports, loadingReconciliations,
    loadImports, loadReconciliations, loadFemaPackages,
  } = useTampaDebrisStore();

  // Force account + documents state
  const [faLabor, setFaLabor] = useState<FALabor[]>([]);
  const [faEquipment, setFaEquipment] = useState<FAEquipment[]>([]);
  const [documents, setDocuments] = useState<DebrisDoc[]>([]);
  const [loadingFA, setLoadingFA] = useState(false);
  const [pendingReview, setPendingReview] = useState(0);

  useEffect(() => {
    loadZones(); loadContractors(); loadTrucks(); loadDisposalSites();
    loadInvoices();
    loadImports(); loadReconciliations(); loadFemaPackages();
  }, [loadZones, loadContractors, loadTrucks, loadDisposalSites, loadInvoices, loadImports, loadReconciliations, loadFemaPackages]);

  // Load force account + documents
  useEffect(() => {
    if (!incidentId) return;
    setLoadingFA(true);
    const FL = FIELDS.debrisForceAccountLabor;
    const FE = FIELDS.debrisForceAccountEquipment;
    const FD = FIELDS.debrisDocuments;

    const incFilter = [{ field: FL.incidentId, operator: 'eq' as const, value: incidentId }];
    const docFilter = [{ field: FD.incidentId, operator: 'eq' as const, value: incidentId }];
    const eqFilter  = [{ field: FE.incidentId, operator: 'eq' as const, value: incidentId }];

    Promise.all([
      queryRecords<Record<string, unknown>>(TABLES.debrisForceAccountLabor, { filters: incFilter, limit: 500 }).then(res =>
        res.records.map(r => ({
          employeeName: String(r[FL.employeeName] ?? ''),
          classification: String(r[FL.classification] ?? ''),
          hoursRegular: Number(r[FL.hoursRegular]) || 0,
          hoursOvertime: Number(r[FL.hoursOvertime]) || 0,
          hourlyRate: Number(r[FL.hourlyRate]) || 0,
          totalCost: Number(r[FL.totalCost]) || 0,
        }))
      ).catch(() => [] as FALabor[]),
      queryRecords<Record<string, unknown>>(TABLES.debrisForceAccountEquipment, { filters: eqFilter, limit: 500 }).then(res =>
        res.records.map(r => ({
          fleetNumber: String(r[FE.fleetNumber] ?? ''),
          equipmentType: String(r[FE.equipmentType] ?? ''),
          hoursUsed: Number(r[FE.hoursUsed]) || 0,
          hourlyRate: Number(r[FE.hourlyRate]) || 0,
          totalCost: Number(r[FE.totalCost]) || 0,
        }))
      ).catch(() => [] as FAEquipment[]),
      queryRecords<Record<string, unknown>>(TABLES.debrisDocuments, { filters: docFilter, limit: 500 }).then(res =>
        res.records.map(r => ({
          documentType: String(r[FD.documentType] ?? ''),
          title: String(r[FD.title] ?? ''),
        }))
      ).catch(() => [] as DebrisDoc[]),
    ]).then(([labor, equipment, docs]) => {
      setFaLabor(labor);
      setFaEquipment(equipment);
      setDocuments(docs);
    }).finally(() => setLoadingFA(false));
  }, [incidentId]);

  // Check for pending review items in localStorage
  useEffect(() => {
    if (!incidentId) return;
    try {
      const raw = localStorage.getItem(`debris-review-pending-${incidentId}`);
      if (raw) {
        const items = JSON.parse(raw);
        setPendingReview(items.filter((i: any) => i.status === 'pending').length);
      } else {
        setPendingReview(0);
      }
    } catch {}
  }, [incidentId]);

  const iZones = incidentId ? zones : [];
  const iContractors = incidentId ? contractors : [];
  const iSites = incidentId ? disposalSites : [];
  const iTrucks = incidentId ? trucks : [];
  const iTickets = incidentId ? allTickets : [];
  const iInvoices = incidentId ? invoices : [];
  const iImports = incidentId ? imports : [];
  const iReconciliations = incidentId ? reconciliations : [];
  const iFemaPackages = incidentId ? femaPackages : [];

  // ── Computed stats ─────────────────────────────────────────
  const getTicketCY = useCallback((t: { calculatedCubicYards: string; loadPercentage: string; truckId: string }) => {
    const stored = parseFloat(t.calculatedCubicYards);
    if (stored > 0) return stored;
    const loadPct = parseFloat(t.loadPercentage) || 0;
    const truck = iTrucks.find(tr => String(tr.id) === t.truckId);
    const cap = truck ? parseFloat(truck.cubicYardCapacity || '0') : 0;
    return cap > 0 ? Math.round(cap * (loadPct / 100)) : 0;
  }, [iTrucks]);

  const totalCY = useMemo(() => iTickets.reduce((sum, t) => sum + getTicketCY(t), 0), [iTickets, getTicketCY]);

  const activeZones = iZones.filter(z => z.status === 'active');
  const passCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    activeZones.forEach(z => {
      const pass = z.currentPass || '1';
      counts[pass] = (counts[pass] || 0) + 1;
    });
    return counts;
  }, [activeZones]);

  const openInvoices = iInvoices.filter(i => i.status !== 'paid');
  const openInvoiceTotal = openInvoices.reduce((s, i) => s + (parseFloat(i.totalAmount) || 0), 0);

  const avgCapacity = useMemo(() => {
    const sites = iSites.filter(s => parseFloat(s.capacityCubicYards || '0') > 0);
    if (sites.length === 0) return 0;
    return sites.reduce((s, site) => {
      const cap = parseFloat(site.capacityCubicYards || '0');
      const vol = parseFloat(site.currentVolume || '0');
      return s + (cap > 0 ? (vol / cap) * 100 : 0);
    }, 0) / sites.length;
  }, [iSites]);

  // ── Force Account Totals ────────────────────────────────────
  const faTotalHours = useMemo(() => faLabor.reduce((s, l) => s + l.hoursRegular + l.hoursOvertime, 0), [faLabor]);
  const faLaborCost = useMemo(() => faLabor.reduce((s, l) => s + l.totalCost, 0), [faLabor]);
  const faEquipmentCost = useMemo(() => faEquipment.reduce((s, e) => s + e.totalCost, 0), [faEquipment]);
  const faTotalCost = faLaborCost + faEquipmentCost;
  const faPersonnelCount = useMemo(() => new Set(faLabor.map(l => l.employeeName).filter(Boolean)).size, [faLabor]);

  // ── Evidence Counts ────────────────────────────────────────
  const evidenceCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    documents.forEach(d => {
      const t = d.documentType || 'other';
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [documents]);
  const truckCerts = evidenceCounts['truck_certification'] || evidenceCounts['truck_cert'] || evidenceCounts['certification'] || 0;
  const ticketImages = evidenceCounts['ticket_image'] || evidenceCounts['ticket_photo'] || evidenceCounts['photo'] || 0;
  const invoiceDocs = evidenceCounts['invoice'] || evidenceCounts['invoice_backup'] || 0;
  const totalDocs = documents.length;

  // ── All-Vendor Spend ────────────────────────────────────────
  const vendorSpend = useMemo(() => {
    const contMap = Object.fromEntries(iContractors.map(c => [String(c.id), c]));
    const byVendor: Record<string, { name: string; spend: number }> = {};
    iInvoices.forEach(inv => {
      const cont = contMap[inv.contractorId];
      const name = cont?.companyName || `Contractor ${inv.contractorId}`;
      if (!byVendor[name]) byVendor[name] = { name, spend: 0 };
      byVendor[name].spend += parseFloat(inv.totalAmount) || 0;
    });
    return Object.values(byVendor).sort((a, b) => b.spend - a.spend);
  }, [iInvoices, iContractors]);
  const totalVendorSpend = vendorSpend.reduce((s, v) => s + v.spend, 0);

  // ── Pipeline Health ─────────────────────────────────────────
  const pipeline = useMemo(() => {
    const totalTickets = iTickets.length;
    const roadMatched = iTickets.filter(t => !!t.roadName).length;
    const unmatched = totalTickets - roadMatched;

    // Trucks with certs: check documents table for truck_certification type
    const trucksWithCerts = new Set<string>();
    documents.forEach(d => {
      if (d.documentType === 'truck_certification' || d.documentType === 'truck_cert' || d.documentType === 'certification') {
        // We don't have truckId in doc, but we can count total certs vs total trucks
        trucksWithCerts.add(d.title); // approximate
      }
    });
    const truckCertCount = truckCerts; // from evidence counts
    const totalTrucks = iTrucks.length;
    const trucksMissingCerts = Math.max(0, totalTrucks - truckCertCount);

    // Invoice pipeline
    const pendingInvoices = iInvoices.filter(i => i.status === 'pending' || i.status === 'draft' || i.status === 'submitted');
    const reconciledInvoices = iInvoices.filter(i => i.status === 'reconciled' || i.status === 'approved' || i.status === 'paid');

    // Reconciliation pipeline
    const reconContractorIds = new Set(iReconciliations.map(r => r.contractorId));
    // Debris contractors (those with load tickets)
    const debrisContractorIds = new Set(iTickets.map(t => t.contractorId).filter(Boolean));
    const unreconciledDebrisContractors = [...debrisContractorIds].filter(id => !reconContractorIds.has(id));
    const draftRecons = iReconciliations.filter(r => r.status === 'draft' || r.status === 'in_review');
    const approvedRecons = iReconciliations.filter(r => r.status === 'approved');
    const disputedRecons = iReconciliations.filter(r => r.status === 'disputed');

    // FEMA pipeline
    const approvedReconIds = new Set(approvedRecons.map(r => String(r.id)));
    // Simple check: do we have any FEMA packages at all?
    const femaBuilt = iFemaPackages.length;
    const femaSubmitted = iFemaPackages.filter(p => p.status === 'submitted_to_fema' || p.status === 'approved').length;

    return {
      totalTickets, roadMatched, unmatched,
      totalTrucks, truckCertCount, trucksMissingCerts,
      pendingInvoices, reconciledInvoices,
      unreconciledDebrisContractors, draftRecons, approvedRecons, disputedRecons,
      debrisContractorIds: debrisContractorIds.size,
      femaBuilt, femaSubmitted,
    };
  }, [iTickets, iTrucks, truckCerts, iInvoices, iReconciliations, iFemaPackages, documents]);

  // ── Action Queue ───────────────────────────────────────────
  const actionItems = useMemo(() => {
    const items: { priority: number; icon: React.ReactNode; label: string; detail: string; color: string; link: string }[] = [];

    // High priority: disputed reconciliations
    if (pipeline.disputedRecons.length > 0) {
      items.push({
        priority: 1,
        icon: <IconAlertTriangle size={16} />,
        label: `${pipeline.disputedRecons.length} disputed reconciliation${pipeline.disputedRecons.length !== 1 ? 's' : ''}`,
        detail: 'Requires resolution before payment can proceed',
        color: 'var(--color-error)',
        link: '/debris/reconciliation',
      });
    }

    // High priority: invoices awaiting review
    if (pipeline.pendingInvoices.length > 0) {
      const totalPending = pipeline.pendingInvoices.reduce((s, i) => s + (parseFloat(i.totalAmount) || 0), 0);
      items.push({
        priority: 2,
        icon: <IconFileInvoice size={16} />,
        label: `${pipeline.pendingInvoices.length} invoice${pipeline.pendingInvoices.length !== 1 ? 's' : ''} awaiting review`,
        detail: `${formatCurrency(totalPending)} pending approval`,
        color: '#d97706',
        link: '/debris/invoices',
      });
    }

    // Medium: draft reconciliations need completion
    if (pipeline.draftRecons.length > 0) {
      items.push({
        priority: 3,
        icon: <IconScale size={16} />,
        label: `${pipeline.draftRecons.length} reconciliation${pipeline.draftRecons.length !== 1 ? 's' : ''} in progress`,
        detail: 'Review and approve or dispute',
        color: '#3b82f6',
        link: '/debris/reconciliation',
      });
    }

    // Medium: debris contractors not yet reconciled
    if (pipeline.unreconciledDebrisContractors.length > 0) {
      const names = pipeline.unreconciledDebrisContractors.slice(0, 3).map(id => {
        const c = iContractors.find(ct => String(ct.id) === id);
        return c?.companyName || `#${id}`;
      });
      items.push({
        priority: 4,
        icon: <IconScale size={16} />,
        label: `${pipeline.unreconciledDebrisContractors.length} contractor${pipeline.unreconciledDebrisContractors.length !== 1 ? 's' : ''} not yet reconciled`,
        detail: names.join(', ') + (pipeline.unreconciledDebrisContractors.length > 3 ? ` +${pipeline.unreconciledDebrisContractors.length - 3} more` : ''),
        color: '#d97706',
        link: '/debris/reconciliation',
      });
    }

    // Medium: approved recons but no FEMA package
    if (pipeline.approvedRecons.length > 0 && pipeline.femaBuilt === 0) {
      items.push({
        priority: 5,
        icon: <IconPackage size={16} />,
        label: `${pipeline.approvedRecons.length} approved reconciliation${pipeline.approvedRecons.length !== 1 ? 's' : ''} — no FEMA package built`,
        detail: 'Ready for FEMA reimbursement package',
        color: '#7c3aed',
        link: '/debris/fema',
      });
    }

    // Low: tickets without road match
    if (pipeline.unmatched > 0) {
      const pct = pipeline.totalTickets > 0 ? ((pipeline.unmatched / pipeline.totalTickets) * 100).toFixed(1) : '0';
      items.push({
        priority: 6,
        icon: <IconRoute size={16} />,
        label: `${pipeline.unmatched.toLocaleString()} ticket${pipeline.unmatched !== 1 ? 's' : ''} without road match`,
        detail: `${pct}% of tickets — affects road ownership reporting`,
        color: '#64748b',
        link: '/debris/analytics',
      });
    }

    // Low: trucks missing certs (only meaningful when contractors are onboarded — trucks are global/shared)
    if (pipeline.trucksMissingCerts > 0 && iContractors.length > 0) {
      items.push({
        priority: 7,
        icon: <IconTruck size={16} />,
        label: `${pipeline.trucksMissingCerts} truck${pipeline.trucksMissingCerts !== 1 ? 's' : ''} missing certification`,
        detail: `${pipeline.truckCertCount} of ${pipeline.totalTrucks} trucks have certs on file`,
        color: '#64748b',
        link: '/debris/trucks',
      });
    }

    return items.sort((a, b) => a.priority - b.priority);
  }, [pipeline, iContractors]);

  // ── Zone Progress Table ────────────────────────────────────
  const zoneProgress = useMemo(() => {
    const cyByZone: Record<string, number> = {};
    iTickets.forEach(t => {
      if (t.zoneId) cyByZone[t.zoneId] = (cyByZone[t.zoneId] || 0) + getTicketCY(t);
    });
    return iZones.map(z => {
      const estCY = parseFloat(z.estimatedCubicYards || '0');
      const actualCY = cyByZone[String(z.id)] || parseFloat(z.actualCubicYards || '0') || 0;
      const pctComplete = estCY > 0 ? Math.min((actualCY / estCY) * 100, 100) : 0;
      return {
        id: z.id, zoneName: z.zoneName || z.zoneCode,
        ercDistrict: '', pass: z.currentPass || '1',
        estCY, actualCY, pctComplete, status: z.status,
      };
    });
  }, [iZones, iTickets, getTicketCY]);

  const [zoneSortField, setZoneSortField] = useState('');
  const [zoneSortDir, setZoneSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleZoneSort = (field: string) => {
    if (zoneSortField === field) setZoneSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setZoneSortField(field); setZoneSortDir('asc'); }
  };
  const sortedZoneProgress = [...zoneProgress].sort((a, b) => {
    if (!zoneSortField) return 0;
    const va = (a as any)[zoneSortField] ?? '';
    const vb = (b as any)[zoneSortField] ?? '';
    const n = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return zoneSortDir === 'asc' ? n : -n;
  });

  // ── Vendor Spend Sort ──────────────────────────────────────
  const [vendorSortField, setVendorSortField] = useState('');
  const [vendorSortDir, setVendorSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleVendorSort = (field: string) => {
    if (vendorSortField === field) setVendorSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setVendorSortField(field); setVendorSortDir('asc'); }
  };
  const sortedVendorSpend = useMemo(() => {
    const list = [...vendorSpend];
    if (!vendorSortField) return list;
    return list.sort((a, b) => {
      let va: any, vb: any;
      if (vendorSortField === 'name') { va = a.name; vb = b.name; }
      else { va = a.spend; vb = b.spend; } // spend and pct sort identically
      const n = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return vendorSortDir === 'asc' ? n : -n;
    });
  }, [vendorSpend, vendorSortField, vendorSortDir]);

  // Recent imports + reconciliations
  const recentImports = iImports.slice(0, 5);
  const recentReconciliations = iReconciliations.slice(0, 5);

  const isLoading = loadingZones || loadingContractors || loadingSites || loadingTickets;

  if (isLoading && iTickets.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  return (
    <Stack gap="md">
      {/* Pending review nudge banner */}
      {pendingReview > 0 && (
        <Paper p="xs" radius="md" withBorder style={{ background: '#fffbeb', borderColor: '#fbbf24' }}>
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs">
              <IconAlertTriangle size={16} color="#d97706" />
              <Text size="sm" fw={600} style={{ color: '#92400e' }}>
                {pendingReview} document{pendingReview !== 1 ? 's' : ''} pending review in Document Intake
              </Text>
            </Group>
            <Button size="xs" onClick={() => navigate('/debris/intake')} rightSection={<IconArrowRight size={12} />}>
              Resume Review
            </Button>
          </Group>
        </Paper>
      )}

      {/* KPI Cards — centered 3-up */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 280px)', gap: 12 }}>
          <KPICard icon={IconLeaf} label="Total CY Documented" value={totalCY.toLocaleString()}
            sub={`${iTickets.length.toLocaleString()} load tickets`} color="#16a34a" onClick={() => navigate('/debris/tickets')} />
          <KPICard icon={IconFileInvoice} label="Open Invoices" value={String(openInvoices.length)}
            sub={formatCurrency(openInvoiceTotal)} color="#d97706" onClick={() => navigate('/debris/invoices')} />
          <KPICard icon={IconBuildingWarehouse} label="Sites Capacity" value={`${avgCapacity.toFixed(0)}%`}
            sub={`${iSites.length} sites, ${iSites.filter(s => s.status === 'open').length} open`} color="#7c3aed"
            onClick={() => navigate('/debris/sites')} />
        </div>
      </div>

      {/* ── Action Queue ─────────────────────────────────────── */}
      {actionItems.length > 0 && (
        <Paper p="sm" radius="md" withBorder style={{ borderLeft: '4px solid #d97706', background: '#fffbf5' }}>
          <Group gap="xs" mb={8}>
            <IconAlertTriangle size={16} color="#d97706" />
            <Text fw={700} size="sm">Action Required ({actionItems.length})</Text>
          </Group>
          <Stack gap={6}>
            {actionItems.map((item, idx) => (
              <Paper key={idx} p="xs" radius="sm" withBorder
                style={{ cursor: 'pointer', borderLeft: `3px solid ${item.color}`, background: '#fff',
                  transition: 'transform 100ms', }}
                onClick={() => navigate(item.link)}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; }}>
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ color: item.color, flexShrink: 0 }}>{item.icon}</span>
                    <div style={{ minWidth: 0 }}>
                      <Text size="sm" fw={600}>{item.label}</Text>
                      <Text size="xs" c="dimmed" truncate>{item.detail}</Text>
                    </div>
                  </Group>
                  <IconArrowRight size={14} color="#94a3b8" style={{ flexShrink: 0 }} />
                </Group>
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}

      {actionItems.length === 0 && iTickets.length > 0 && (
        <Paper p="md" radius="md" withBorder style={{ borderLeft: '4px solid var(--color-success)', background: '#f0fdf4', textAlign: 'center' }}>
          <Group gap="xs" justify="center">
            <IconCircleCheck size={18} color="var(--color-success)" />
            <Text fw={600} size="sm" c="var(--color-success)">All caught up — no action items</Text>
          </Group>
        </Paper>
      )}

      {/* ── Pipeline Health ──────────────────────────────────── */}
      <Paper p="sm" radius="md" withBorder>
        <Text fw={700} mb={8} size="sm">Pipeline Health</Text>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6 }}>
          <PipelineStage label="Ingested" count={pipeline.totalTickets}
            completed={pipeline.totalTickets} total={pipeline.totalTickets}
            color="#0284c7" onClick={() => navigate('/debris/tickets')} />
          <PipelineStage label="Road Matched" count={pipeline.roadMatched}
            completed={pipeline.roadMatched} total={pipeline.totalTickets}
            color={pipeline.unmatched > 0 ? '#d97706' : '#16a34a'} onClick={() => navigate('/debris/analytics')} />
          <PipelineStage label="Invoiced" count={iInvoices.length}
            completed={iInvoices.length} total={Math.max(iInvoices.length, 1)}
            color="#7c3aed" onClick={() => navigate('/debris/invoices')} />
          <PipelineStage label="Reconciled" count={pipeline.approvedRecons.length}
            completed={pipeline.approvedRecons.length} total={Math.max(pipeline.debrisContractorIds, 1)}
            color={pipeline.approvedRecons.length > 0 ? '#16a34a' : '#d97706'} onClick={() => navigate('/debris/reconciliation')} />
          <PipelineStage label="FEMA Built" count={pipeline.femaBuilt}
            completed={pipeline.femaBuilt} total={Math.max(pipeline.femaBuilt, 1)}
            color={pipeline.femaBuilt > 0 ? '#16a34a' : '#94a3b8'} onClick={() => navigate('/debris/fema')} />
          <PipelineStage label="FEMA Submitted" count={pipeline.femaSubmitted}
            completed={pipeline.femaSubmitted} total={Math.max(pipeline.femaBuilt, 1)}
            color={pipeline.femaSubmitted > 0 ? '#16a34a' : '#94a3b8'} onClick={() => navigate('/debris/fema')} />
        </div>
      </Paper>

      {/* Workforce Summary + Evidence Completeness */}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* Workforce Summary */}
        <Paper p="sm" radius="md" withBorder>
          <Text fw={700} mb={8} size="sm">Workforce Summary</Text>
          {loadingFA ? (
            <Center h={60}><Loader size="sm" /></Center>
          ) : (
            <SimpleGrid cols={3} style={{ alignItems: 'stretch' }}>
              <Paper p="xs" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #0284c7' }}>
                <Group gap={4} justify="center" mb={2}><IconUsers size={13} color="#0284c7" /></Group>
                <Text fz={20} fw={800} c="#0284c7">{faPersonnelCount > 0 ? faPersonnelCount : '--'}</Text>
                <Text size="xs" c="dimmed">{faPersonnelCount > 0 ? 'Personnel' : 'Import roster'}</Text>
              </Paper>
              <Paper p="xs" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #d97706' }}>
                <Group gap={4} justify="center" mb={2}><IconClock size={13} color="#d97706" /></Group>
                <Text fz={20} fw={800} c="#d97706">{faTotalHours > 0 ? faTotalHours.toLocaleString() : '--'}</Text>
                <Text size="xs" c="dimmed">FA Hours</Text>
              </Paper>
              <Paper p="xs" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #16a34a' }}>
                <Group gap={4} justify="center" mb={2}><IconCurrencyDollar size={13} color="#16a34a" /></Group>
                <Text fz={20} fw={800} c="#16a34a">{faTotalCost > 0 ? formatCurrency(faTotalCost) : '--'}</Text>
                <Text size="xs" c="dimmed">FA Cost</Text>
              </Paper>
            </SimpleGrid>
          )}
        </Paper>

        {/* Evidence Completeness */}
        <Paper p="sm" radius="md" withBorder>
          <Group justify="space-between" mb={8}>
            <Text fw={700} size="sm">Documentation Completeness</Text>
            <Badge size="xs" color={totalDocs > 0 ? 'var(--color-success)' : '#939598'}>
              {totalDocs} total docs
            </Badge>
          </Group>
          {loadingFA ? (
            <Center h={60}><Loader size="sm" /></Center>
          ) : (
            <Stack gap={6}>
              <EvidenceRow label="Truck Certifications" count={truckCerts} icon={<IconShieldCheck size={14} />} />
              <EvidenceRow label="Ticket Image Packages" count={ticketImages} icon={<IconFileInvoice size={14} />} />
              <EvidenceRow label="Invoice Documentation" count={invoiceDocs} icon={<IconCurrencyDollar size={14} />} />
              {totalDocs === 0 && (
                <Text size="xs" c="dimmed" ta="center" mt="xs">No supporting documents uploaded yet</Text>
              )}
            </Stack>
          )}
        </Paper>
      </SimpleGrid>

      {/* All-Vendor Spend + Recent Activity */}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* All-Vendor Spend */}
        {vendorSpend.length > 0 && (
          <Paper p="sm" radius="md" withBorder>
            <Group justify="space-between" mb={8}>
              <Text fw={700} size="sm">All-Vendor Spend</Text>
              <Text size="sm" fw={700} c="var(--color-primary-700)">{formatCurrency(totalVendorSpend)} total</Text>
            </Group>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 220 }}>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc', cursor: 'pointer', userSelect: 'none', color: vendorSortField === 'name' ? '#4B3FFF' : '#64748b' }} onClick={() => toggleVendorSort('name')}>
                      Vendor {vendorSortField === 'name' ? (vendorSortDir === 'desc' ? '\u2193' : '\u2191') : <span style={{opacity:0.25}}>{'\u2191'}</span>}
                    </Table.Th>
                    <Table.Th ta="right" style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc', cursor: 'pointer', userSelect: 'none', color: vendorSortField === 'spend' ? '#4B3FFF' : '#64748b' }} onClick={() => toggleVendorSort('spend')}>
                      Spend {vendorSortField === 'spend' ? (vendorSortDir === 'desc' ? '\u2193' : '\u2191') : <span style={{opacity:0.25}}>{'\u2191'}</span>}
                    </Table.Th>
                    <Table.Th ta="right" style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc', cursor: 'pointer', userSelect: 'none', color: vendorSortField === 'pct' ? '#4B3FFF' : '#64748b' }} onClick={() => toggleVendorSort('pct')}>
                      % {vendorSortField === 'pct' ? (vendorSortDir === 'desc' ? '\u2193' : '\u2191') : <span style={{opacity:0.25}}>{'\u2191'}</span>}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sortedVendorSpend.slice(0, 5).map(v => (
                    <Table.Tr key={v.name}>
                      <Table.Td><Text size="sm" fw={500}>{v.name}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" ff="monospace">{formatCurrency(v.spend)}</Text></Table.Td>
                      <Table.Td ta="right">
                        <Text size="sm" c="dimmed">{totalVendorSpend > 0 ? ((v.spend / totalVendorSpend) * 100).toFixed(1) : 0}%</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
            {vendorSpend.length > 5 && (
              <Text size="xs" c="dimmed" ta="center" mt="xs" style={{ cursor: 'pointer' }}
                onClick={() => navigate('/debris/vendors')}>
                + {vendorSpend.length - 5} more vendors
              </Text>
            )}
          </Paper>
        )}

        {/* Recent Activity */}
        <Paper p="sm" radius="md" withBorder>
          <Text fw={700} mb={8} size="sm">Recent Activity</Text>
          <Stack gap={6}>
            {recentImports.length === 0 && recentReconciliations.length === 0 && (
              <Text c="dimmed" size="sm">No recent activity</Text>
            )}
            {recentImports.slice(0, 3).map(imp => (
              <Paper key={`imp-${imp.id}`} p="xs" radius="sm" withBorder
                style={{ borderLeft: `3px solid ${IMPORT_STATUS_COLORS[imp.status] ?? '#939598'}`, cursor: 'pointer' }}
                onClick={() => navigate('/debris/processor')}>
                <Group justify="space-between" wrap="nowrap">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} truncate>{imp.fileName || 'Unnamed file'}</Text>
                    <Text size="xs" c="dimmed">{imp.recordCount} records imported</Text>
                  </div>
                  <Badge size="xs" color={IMPORT_STATUS_COLORS[imp.status] ?? '#939598'}>{imp.status}</Badge>
                </Group>
              </Paper>
            ))}
            {recentReconciliations.slice(0, 3).map(rec => {
              const cont = iContractors.find(c => String(c.id) === rec.contractorId);
              return (
                <Paper key={`rec-${rec.id}`} p="xs" radius="sm" withBorder
                  style={{ borderLeft: `3px solid ${STATUS_COLORS[rec.status] ?? '#939598'}`, cursor: 'pointer' }}
                  onClick={() => { window.location.href = `/debris/reconciliation?view=${rec.id}`; }}>
                  <Group justify="space-between" wrap="nowrap">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={500} truncate>{cont?.companyName || `Contractor ${rec.contractorId}`}</Text>
                      <Text size="xs" c="dimmed">
                        Reconciliation — {rec.variancePercent !== 0 ? `${rec.variancePercent.toFixed(1)}% variance` : 'no variance'}
                      </Text>
                    </div>
                    <Badge size="xs" color={STATUS_COLORS[rec.status] ?? '#939598'}>{rec.status.replace(/_/g, ' ')}</Badge>
                  </Group>
                </Paper>
              );
            })}
          </Stack>
        </Paper>
      </SimpleGrid>

    </Stack>
  );
}

// ── KPI Card Component ──────────────────────────────────────
function KPICard({ icon: Icon, label, value, sub, color, onClick }: {
  icon: React.ComponentType<{ size?: number }>;
  label: string; value: string; sub: string; color: string;
  onClick: () => void;
}) {
  return (
    <Paper p="sm" radius="md" withBorder ta="center"
      style={{
        cursor: 'pointer', borderRadius: 12, borderTop: `3px solid ${color}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 2, transition: 'transform 150ms, box-shadow 150ms', padding: '12px 16px',
      }}
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; }}>
      <Icon size={20} style={{ color }} />
      <Text size="xs" tt="uppercase" fw={700} c="dimmed">{label}</Text>
      <Text fz={28} fw={800} style={{ color, lineHeight: 1.1 }}>{value}</Text>
      <Text size="xs" c="#94a3b8">{sub}</Text>
    </Paper>
  );
}

// ── Evidence Row Component ──────────────────────────────────
function EvidenceRow({ label, count, icon }: { label: string; count: number; icon: React.ReactNode }) {
  return (
    <Group justify="space-between">
      <Group gap="xs">
        {icon}
        <Text size="sm">{label}</Text>
      </Group>
      <Badge size="sm" variant={count > 0 ? 'filled' : 'light'}
        color={count > 0 ? 'var(--color-success)' : '#939598'}>
        {count} uploaded
      </Badge>
    </Group>
  );
}

// ── Pipeline Stage Component ────────────────────────────────
function PipelineStage({ label, count, completed, total, color, onClick }: {
  label: string; count: number; completed: number; total: number; color: string;
  onClick: () => void;
}) {
  const pct = total > 0 ? Math.min((completed / total) * 100, 100) : 0;
  const isComplete = pct >= 99.5 && total > 0;
  return (
    <Paper radius="md" withBorder ta="center"
      style={{ cursor: 'pointer', borderTop: `3px solid ${color}`, transition: 'transform 100ms', padding: '6px 8px' }}
      onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; }}>
      <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={1} style={{ fontSize: 10 }}>{label}</Text>
      <Text fz={18} fw={800} style={{ color, lineHeight: 1.1 }}>{count.toLocaleString()}</Text>
      {total > 0 && (
        <>
          <Progress value={pct} size={3} radius="xl" color={isComplete ? 'var(--color-success)' : color}
            mt={3} mb={1} />
          <Text size="xs" c={isComplete ? 'var(--color-success)' : 'dimmed'} fw={isComplete ? 600 : 400} style={{ fontSize: 10 }}>
            {isComplete ? 'Complete' : `${pct.toFixed(0)}%`}
          </Text>
        </>
      )}
    </Paper>
  );
}
